class LuluSiteChrome {
  constructor(header) {
    this.header = header;
    this.drawer = header.querySelector('[data-lulu-site-drawer]');
    this.panel = header.querySelector('.lulu-site__drawer');
    this.closeButtons = header.querySelectorAll('[data-lulu-site-drawer-close]');
    this.drawerLinks = this.drawer?.querySelectorAll('a') ?? [];
    this.rootView = header.querySelector('[data-lulu-drawer-root]');
    this.submenuButtons = header.querySelectorAll('[data-lulu-drawer-open]');
    this.backButtons = header.querySelectorAll('[data-lulu-drawer-back]');
    this.activeSubmenu = null;
    this.activeSubmenuTrigger = null;
    this.scrollPosition = 0;
    this.wasDrawerOpen = false;
    this.restoreScrollOnClose = true;
    this.cartLink = header.querySelector('[data-lulu-site-cart]');
    this.cartCount = header.querySelector('[data-lulu-site-cart-count]');
    this.cartRequestId = 0;
    this.boundCloseDrawer = this.closeDrawer.bind(this);
    this.boundDrawerToggle = this.onDrawerToggle.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundCartUpdate = this.onCartUpdate.bind(this);
    this.boundDrawerNavigation = this.onDrawerNavigation.bind(this);
    this.boundOpenSubmenu = (event) => this.openSubmenu(event.currentTarget);
    this.boundReturnToRoot = () => this.returnToRoot();

    this.closeButtons.forEach((button) => button.addEventListener('click', this.boundCloseDrawer));
    this.drawerLinks.forEach((link) => link.addEventListener('click', this.boundDrawerNavigation));
    this.submenuButtons.forEach((button) => button.addEventListener('click', this.boundOpenSubmenu));
    this.backButtons.forEach((button) => button.addEventListener('click', this.boundReturnToRoot));
    this.drawer?.addEventListener('toggle', this.boundDrawerToggle);
    document.addEventListener('keydown', this.boundKeydown);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.boundCartUpdate);
    }
    this.syncDrawerState();
    this.initHeaderState();
  }

  initHeaderState() {
    const hero = document.querySelector('[data-lulu-site-hero], .lulu-editorial__hero');
    if (!hero || !('IntersectionObserver' in window)) return;

    this.headerObserver = new IntersectionObserver(
      ([entry]) => this.header.classList.toggle('is-scrolled', !entry.isIntersecting),
      { threshold: 0, rootMargin: '-1px 0px 0px 0px' },
    );
    this.headerObserver.observe(hero);
  }

  closeDrawer({ restoreScroll = true } = {}) {
    if (!this.drawer?.open) return;
    this.restoreScrollOnClose = restoreScroll;
    this.returnToRoot({ focusTrigger: false });
    this.drawer?.removeAttribute('open');
    this.syncDrawerState();
  }

  syncDrawerState() {
    if (this.panel) this.panel.inert = !this.drawer?.open;
  }

  onDrawerToggle() {
    const isOpen = Boolean(this.drawer?.open);
    this.syncDrawerState();

    if (isOpen && !this.wasDrawerOpen) this.scrollPosition = window.scrollY;
    if (!isOpen && this.wasDrawerOpen) {
      this.returnToRoot({ focusTrigger: false });
      if (this.restoreScrollOnClose) this.restoreScrollPosition();
      this.restoreScrollOnClose = true;
    }
    this.wasDrawerOpen = isOpen;
  }

  restoreScrollPosition() {
    window.requestAnimationFrame(() => window.scrollTo(0, this.scrollPosition));
  }

  openSubmenu(trigger) {
    const submenu = this.header.querySelector(`#${trigger.getAttribute('aria-controls')}`);
    if (!submenu || !this.rootView) return;

    this.activeSubmenu = submenu;
    this.activeSubmenuTrigger = trigger;
    this.rootView.hidden = true;
    this.rootView.inert = true;
    submenu.hidden = false;
    submenu.inert = false;
    trigger.setAttribute('aria-expanded', 'true');
    window.requestAnimationFrame(() => submenu.querySelector('[data-lulu-drawer-back]')?.focus());
  }

  returnToRoot({ focusTrigger = true } = {}) {
    if (!this.activeSubmenu || !this.rootView) return;

    const trigger = this.activeSubmenuTrigger;
    this.activeSubmenu.hidden = true;
    this.activeSubmenu.inert = true;
    this.rootView.hidden = false;
    this.rootView.inert = false;
    trigger?.setAttribute('aria-expanded', 'false');
    this.activeSubmenu = null;
    this.activeSubmenuTrigger = null;
    if (focusTrigger) trigger?.focus();
  }

  onKeydown(event) {
    if (event.key !== 'Escape' || !this.drawer?.open) return;
    event.preventDefault();
    if (this.activeSubmenu) {
      this.returnToRoot();
      return;
    }
    this.closeDrawer();
  }

  onDrawerNavigation() {
    this.closeDrawer({ restoreScroll: false });
  }

  onCartUpdate(event) {
    const itemCount = event?.cartData?.item_count;
    if (Number.isInteger(itemCount)) {
      this.syncCartCount(itemCount);
      return Promise.resolve();
    }

    return this.refreshCartCount();
  }

  async refreshCartCount() {
    const requestId = ++this.cartRequestId;
    const cartUrl = window.routes?.cart_url || '/cart';

    try {
      const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cartData = await response.json();
      if (requestId === this.cartRequestId) this.syncCartCount(cartData.item_count);
    } catch {
      // The cart action already succeeded; leave the last known count in place if refresh fails.
    }
  }

  syncCartCount(itemCount) {
    const count = Math.max(0, Number(itemCount) || 0);
    if (this.cartCount) {
      this.cartCount.textContent = count > 0 ? String(count) : '';
      this.cartCount.hidden = count === 0;
    }
    this.cartLink?.setAttribute('aria-label', `Cart with ${count} ${count === 1 ? 'item' : 'items'}`);
  }

  destroy() {
    this.cartRequestId += 1;
    this.cartUpdateUnsubscriber?.();
    this.headerObserver?.disconnect();
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.boundCloseDrawer));
    this.drawerLinks.forEach((link) => link.removeEventListener('click', this.boundDrawerNavigation));
    this.submenuButtons.forEach((button) => button.removeEventListener('click', this.boundOpenSubmenu));
    this.backButtons.forEach((button) => button.removeEventListener('click', this.boundReturnToRoot));
    this.drawer?.removeEventListener('toggle', this.boundDrawerToggle);
    document.removeEventListener('keydown', this.boundKeydown);
    this.closeDrawer({ restoreScroll: false });
    this.header.classList.remove('is-scrolled');
  }
}

const initializeLuluSiteChrome = (scope = document) => {
  const headers = [...scope.querySelectorAll('[data-lulu-site-header]')];
  if (scope.matches?.('[data-lulu-site-header]')) headers.unshift(scope);

  headers.forEach((header) => {
    header.luluSiteChrome?.destroy();
    header.luluSiteChrome = new LuluSiteChrome(header);
  });
};

initializeLuluSiteChrome();

document.addEventListener('shopify:section:load', (event) => initializeLuluSiteChrome(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  const headers = [...event.target.querySelectorAll('[data-lulu-site-header]')];
  if (event.target.matches?.('[data-lulu-site-header]')) headers.unshift(event.target);
  headers.forEach((header) => header.luluSiteChrome?.destroy());
});
